import Button from "components/button"
import { Checkbox } from "components/check-box/Checkbox"
import Input from "components/input"
import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import request from "request"
import { useDispatch, useSelector } from "store/hooks"
import type users from "../users"
import styles from "./create-modal.module.scss"

type ValidationError = {
  name: "email" | "password1" | "password2" | "role"
  value: string
} | null

interface UpdateUser {
  email?: string
  password?: string
  role?: string
  permissions?: string[]
}

const UpdateRoleModal = ({
  onClose,
  onEdit,
  selectedUser,
  roles,
}: {
  selectedUser?: users.user
  onClose: () => void
  onEdit: () => void
  roles: users.roles[]
}) => {
  const dispatch = useDispatch()
  const rolePermissionState = useSelector((state) => state.permissions.data)

  const [email, setEmail] = useState(selectedUser?.email ?? "")
  const [role, setRole] = useState(selectedUser?.role ?? "")
  const [pending, setPending] = useState(false)
  const [validError, setValidError] = useState<ValidationError>(null)
  const [userPermission, setUserPermission] = useState<
    { title: string; value: string; checked: boolean }[]
  >([])

  const blocked = role === selectedUser?.role

  useEffect(() => {
    if (!role) return

    setUserPermission([])

    if (role === "custom") {
      dispatch.permissions.getAllPermissions()
    } else {
      dispatch.permissions.rolePermission({ role: role as string })
    }
  }, [role, dispatch])

  useEffect(() => {
    if (!role || !rolePermissionState) return

    if (role === "custom") {
      const allPermissions = Array.isArray(rolePermissionState)
        ? rolePermissionState.map((permission) => {
            return {
              title: permission.name || permission.title || permission,
              value: permission.key || permission.value || permission,
              checked: false,
            }
          })
        : []
      setUserPermission(allPermissions)
    } else {
      const backendPermissions: string[] = Array.isArray(rolePermissionState)
        ? rolePermissionState
        : []

      const allPermissions = Array.isArray(rolePermissionState)
        ? rolePermissionState.map((permission) => ({
            title: permission.name || permission,
            value: permission.key || permission,
            checked: backendPermissions.includes(permission.key || permission),
          }))
        : []
      setUserPermission(allPermissions)
    }
  }, [role, rolePermissionState])

  const handleEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!role) {
      return setValidError({ name: "role", value: "Role is required" })
    } else {
      setValidError(null)
    }

    setPending(true)
    let permissions: string[] = []
    if (role === "custom") {
      permissions = userPermission
        .filter((item) => item.checked && item.value)
        .map((item) => item.value)
    }

    const body: UpdateUser = { email: email.toLowerCase(), role, permissions }

    request
      .put("/admin/users/" + selectedUser?.id, body)
      .then(() => onEdit())
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error editing user: ${errorMessage}`)
      })
      .finally(() => setPending(false))
  }

  const handlePermissionChange = (idx: number) => {
    if (role !== "custom") return

    const newPermissions = [...userPermission]
    newPermissions[idx] = {
      ...newPermissions[idx],
      checked: !newPermissions[idx].checked,
    }
    setUserPermission(newPermissions)
  }

  const user = useSelector((state) => state.user)

  const getAvailableRoles = useCallback((): [string, string][] => {
    const currentRole = user?.role

    if (currentRole === "root") {
      return roles.map((role) => [String(role), String(role)])
    }

    if (currentRole === "admin") {
      return roles
        .filter((role) => role === "expert" || role === "custom")
        .map((role) => [String(role), String(role)])
    }

    if (currentRole === "custom") {
      const hasCreateUserPermission = user?.permissions?.includes("create_user")
      if (hasCreateUserPermission) {
        return roles
          .filter((role) => role === "expert")
          .map((role) => [String(role), String(role)])
      }
      return []
    }

    return []
  }, [user?.role, user?.permissions, roles])

  const canCreateCustomRole = user?.role === "root" || user?.role === "admin"

  return (
    <div className={styles.modal} onMouseDown={onClose}>
      <form
        onSubmit={handleEdit}
        className={styles.modalWrap}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.title}>Edit role</div>
        <div className={styles.inputs}>
          <div className={styles.input}>
            <Input
              title="Email"
              type="string"
              value={email}
              onInput={(value) => setEmail(value)}
              disabled={true}
            />
          </div>
          <div className={styles.input}>
            <Input
              id="role"
              title="Role"
              type="select"
              value={role}
              onSelect={(option) => setRole(option.key)}
              options={getAvailableRoles()
                .filter(([_, value]) => {
                  if (value === "custom") {
                    return canCreateCustomRole
                  }
                  return true
                })
                .map(([value, key]) => ({
                  value: value,
                  key: key,
                }))}
            />
            {validError?.name === "role" && (
              <div className={styles.inputError}>{validError.value}</div>
            )}
          </div>

          {role && (
            <>
              <div className={styles.title}>
                {role === "custom" ? "Access settings" : "Role access"}
              </div>
              <div className={styles.inputs}>
                <div className={styles.input}>
                  {userPermission?.map((item, idx) => (
                    <Checkbox
                      key={item?.value || `permission-${idx}`}
                      checked={item?.checked}
                      title={item?.title}
                      disabled={role !== "custom"}
                      onChange={() => handlePermissionChange(idx)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.buttonGroup}>
          <Button color="orange" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="green"
            pending={pending}
            blocked={blocked}
          >
            Edit
          </Button>
        </div>
      </form>
    </div>
  )
}

export default UpdateRoleModal
